import { omit } from "lodash";

import { UpdateUserAccessPolicySchema } from "../../../api";
import { createPatch } from "../../../utils/patch";
import { AccessPoliciesItem } from "./access-policies";
import {
  getCountriesFromLocations,
  getRegionsFromLocations,
  Location,
} from "./utils";

/**
 * The editable part of an access policy. Field names match
 * `UpdateUserAccessPolicySchema` so a patch can be sent as-is - except
 * `locations`, which the API takes as separate `countries` / `regions` and
 * {@link policyPatchBody} translates.
 *
 * A type alias, not an interface: `createPatch` takes a
 * `Record<string, unknown>`, and only aliases get an implicit index signature.
 */
export type Fields = {
  monitoring_active: boolean;
  permissions: number[];
  locations: Location[];
  clinic_uuids: string[];
  physician_uuids: string[];
  visible_patient_ids: number[];
};

/** Empty draft when the user has no policy, so the dialog still renders. */
export function buildInitialFields(policy?: AccessPoliciesItem): Fields {
  return {
    monitoring_active: policy?.monitoring_active ?? false,
    permissions: [...(policy?.permissions ?? [])],
    locations: [...(policy?.locations ?? [])],
    clinic_uuids: (policy?.clinics ?? []).map((c) => c.uuid),
    physician_uuids: (policy?.physicians ?? []).map((p) => p.uuid),
    visible_patient_ids: (policy?.visible_patients ?? []).map(
      (p) => p.patient_id,
    ),
  };
}

/**
 * Body for `PUT /policies/{uuid}` carrying only what changed, or `null` when
 * nothing did.
 */
export function policyPatchBody(
  fields: Fields,
  initial: Fields,
): UpdateUserAccessPolicySchema | null {
  const patch = createPatch(fields, initial);
  if (Object.keys(patch).length === 0) return null;

  const body: UpdateUserAccessPolicySchema = omit(patch, "locations");
  if ("locations" in patch) {
    body.countries = getCountriesFromLocations(fields.locations);
    body.regions = getRegionsFromLocations(fields.locations);
  }
  return body;
}
